/* ======================================================================
   REGISTA — HEADER & FOOTER REDESIGN, behavioural bits
   ----------------------------------------------------------------------
   Vanilla, no dependencies. Everything here is a no-op unless
   <body class="rg-redesign"> is present, so removing that one class
   disables this file along with the stylesheet.

   Three jobs:
     1. Mega-menu horizontal scroll indicator (1024 and below).
     2. One country open at a time in the mobile drawer (level 3).
     3. Mobile footer accordion.
   ====================================================================== */

(function () {
  'use strict';

  var ROOT_CLASS = 'rg-redesign';

  function enabled() {
    return document.body && document.body.classList.contains(ROOT_CLASS);
  }

  /* ------------------------------------------------------------------
     1. Mega-menu scroll indicator
     The track is .mega-menu::after; its thumb is a background layer whose
     width and offset are driven by these two custom properties. No extra
     DOM, so nothing to clean up if the kill switch is thrown.
     ------------------------------------------------------------------ */

  function updateScrollIndicator(track) {
    var panel = track.closest('.mega-menu');
    if (!panel) return;

    var scrollable = track.scrollWidth - track.clientWidth;

    // Drives whether the indicator is drawn at all — a bar that cannot move
    // is noise, and a panel narrower than the header has nothing to scroll.
    panel.dataset.rgScrollable = scrollable > 1 ? 'true' : 'false';

    if (scrollable <= 1) {
      panel.style.setProperty('--rg-track-thumb-width', '100%');
      panel.style.setProperty('--rg-track-thumb-offset', '100%');
      return;
    }

    // Math.abs: in RTL, scrollLeft counts negative away from the start edge
    // in Chrome/Safari and positive-from-zero in older engines. Either way
    // the magnitude is the distance travelled.
    var progress = Math.min(Math.abs(track.scrollLeft) / scrollable, 1);
    var ratio = Math.max(track.clientWidth / track.scrollWidth, 0.08);

    panel.style.setProperty('--rg-track-thumb-width', (ratio * 100).toFixed(2) + '%');
    // 100% puts the thumb at the right edge, which is where RTL starts.
    panel.style.setProperty('--rg-track-thumb-offset', ((1 - progress) * 100).toFixed(2) + '%');
  }

  function initScrollIndicators() {
    var tracks = document.querySelectorAll('.mega-menu .mega-menu__linklist');

    Array.prototype.forEach.call(tracks, function (track) {
      if (track.dataset.rgScrollBound === 'true') return;
      track.dataset.rgScrollBound = 'true';

      var onScroll = function () { updateScrollIndicator(track); };

      track.addEventListener('scroll', onScroll, { passive: true });
      updateScrollIndicator(track);

      if (typeof ResizeObserver === 'function') {
        new ResizeObserver(onScroll).observe(track);
      } else {
        window.addEventListener('resize', onScroll);
      }

      // The panel is hidden until its parent <details> opens, so widths read
      // as zero on first paint. Recalculate whenever it opens — three times,
      // because rAF alone is unreliable: it is throttled in a background tab,
      // and the panel has an opening animation that changes its width after
      // the first frame.
      var details = track.closest('details');
      if (details) {
        details.addEventListener('toggle', function () {
          if (!details.open) return;
          onScroll();
          window.requestAnimationFrame(onScroll);
          window.setTimeout(onScroll, 150);
        });
      }
    });
  }

  /* ------------------------------------------------------------------
     2. Drawer level 3 — only one country open at a time

     These are plain native <details>: theme.js registers every disclosure
     class without { extends: "details" }, so <details is="accordion-
     disclosure"> is never upgraded and no theme JS is managing them.
     A native 'toggle' listener is therefore both safe and sufficient.
     ------------------------------------------------------------------ */

  function initSingleOpenAccordions() {
    var panels = document.querySelectorAll('.header-sidebar__sub-panel');

    Array.prototype.forEach.call(panels, function (panel) {
      if (panel.dataset.rgAccordionBound === 'true') return;
      panel.dataset.rgAccordionBound = 'true';

      panel.addEventListener('toggle', function (event) {
        var opened = event.target;
        if (!enabled()) return;
        if (!opened || opened.tagName !== 'DETAILS' || !opened.open) return;

        var siblings = panel.querySelectorAll('details[open]');
        Array.prototype.forEach.call(siblings, function (other) {
          if (other !== opened) other.open = false;
        });
      }, true); // capture: 'toggle' does not bubble
    });
  }

  /* ------------------------------------------------------------------
     3. Mobile footer accordion
     Built once at any width; the stylesheet decides whether it behaves as
     an accordion (≤699px) or as plain columns (above that).
     ------------------------------------------------------------------ */

  function buildFooterAccordion() {
    var blocks = document.querySelectorAll(
      '.footer__block--links, .footer__block--payment_logo'
    );

    Array.prototype.forEach.call(blocks, function (block, index) {
      if (block.dataset.rgAccBuilt === 'true') return;

      var inner = block.firstElementChild;
      if (!inner) return;

      var head = inner.firstElementChild;
      if (!head) return;

      var rest = Array.prototype.slice.call(inner.children, 1);
      if (!rest.length) return;

      block.dataset.rgAccBuilt = 'true';
      block.classList.add('rg-acc');

      var body = document.createElement('div');
      body.className = 'rg-acc__body';
      rest.forEach(function (node) { body.appendChild(node); });
      inner.appendChild(body);

      head.classList.add('rg-acc__head');
      head.setAttribute('role', 'button');
      head.setAttribute('tabindex', '0');

      var toggle = document.createElement('span');
      toggle.className = 'rg-acc__toggle';
      toggle.setAttribute('aria-hidden', 'true');
      head.appendChild(toggle);

      var setOpen = function (open) {
        if (open) {
          block.setAttribute('data-rg-open', '');
        } else {
          block.removeAttribute('data-rg-open');
        }
        head.setAttribute('aria-expanded', open ? 'true' : 'false');
        toggle.textContent = open ? '−' : '+';   /* − / + */
      };

      // Default state: the first column open, the rest closed.
      setOpen(index === 0 || block.classList.contains('footer__block--payment_logo'));

      head.addEventListener('click', function () {
        if (!enabled()) return;
        setOpen(!block.hasAttribute('data-rg-open'));
      });

      head.addEventListener('keydown', function (event) {
        if (!enabled()) return;
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        setOpen(!block.hasAttribute('data-rg-open'));
      });
    });
  }

  /* ------------------------------------------------------------------ */

  function init() {
    if (!enabled()) return;
    initScrollIndicators();
    initSingleOpenAccordions();
    buildFooterAccordion();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  // Theme editor re-renders sections without a full page load.
  document.addEventListener('shopify:section:load', init);
})();
